'use client';

import { useEffect, useRef, useState } from 'react';
import { useLoadedAvifSource } from '@/components/tools/avif-tool-shared';
import { consumeToolUsage, formatToolCreditSuccess } from '@/lib/browser-tool-usage';
import {
  formatFileSize,
  recompressStillAvif,
  type AvifEffortPreset,
} from '@/lib/browser-avif-tools';
import { getBaseName } from '@/lib/browser-image-tools';

export default function BrowserAvifQualityRecompressor() {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const { source, loadSource, clearSource, isSupported } = useLoadedAvifSource();
  const [quality, setQuality] = useState(68);
  const [effortPreset, setEffortPreset] = useState<AvifEffortPreset>('balanced');
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
  const [resultPreviewUrl, setResultPreviewUrl] = useState<string | null>(null);
  const [downloadName, setDownloadName] = useState<string | null>(null);
  const [resultSize, setResultSize] = useState<number | null>(null);
  const [isDragActive, setIsDragActive] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [creditMessage, setCreditMessage] = useState<string | null>(null);

  useEffect(() => {
    return () => {
      [downloadUrl, resultPreviewUrl].forEach((url) => {
        if (url) {
          URL.revokeObjectURL(url);
        }
      });
    };
  }, [downloadUrl, resultPreviewUrl]);

  function resetOutputState() {
    [downloadUrl, resultPreviewUrl].forEach((url) => {
      if (url) {
        URL.revokeObjectURL(url);
      }
    });

    setDownloadUrl(null);
    setResultPreviewUrl(null);
    setDownloadName(null);
    setResultSize(null);
  }

  async function handleFileSelection(file: File | null) {
    resetOutputState();
    setErrorMessage(null);
    setCreditMessage(null);

    if (!file) {
      clearSource();
      return;
    }

    try {
      await loadSource(file);
    } catch (error) {
      clearSource();
      setErrorMessage(
        error instanceof Error ? error.message : 'The AVIF file could not be opened.',
      );
    }
  }

  async function runRecompression() {
    if (!source) {
      setErrorMessage('Choose one AVIF first.');
      return;
    }

    setIsProcessing(true);
    setErrorMessage(null);
    setCreditMessage(null);

    try {
      const credits = await consumeToolUsage(
        'avif-quality-recompressor',
        'Credits are not available for another AVIF recompression right now.',
      );
      const result = await recompressStillAvif({
        file: source.file,
        quality,
        effortPreset,
      });
      const nextDownloadUrl = URL.createObjectURL(result.blob);
      const nextPreviewUrl = URL.createObjectURL(result.previewBlob);

      resetOutputState();
      setDownloadUrl(nextDownloadUrl);
      setResultPreviewUrl(nextPreviewUrl);
      setDownloadName(`${getBaseName(source.file.name)}-recompressed.avif`);
      setResultSize(result.blob.size);
      setCreditMessage(formatToolCreditSuccess('AVIF recompression', credits));
    } catch (error) {
      setErrorMessage(
        error instanceof Error ? error.message : 'The AVIF file could not be recompressed.',
      );
    } finally {
      setIsProcessing(false);
    }
  }

  const sizeDelta =
    source && resultSize !== null
      ? `${Math.round((1 - resultSize / Math.max(source.file.size, 1)) * 100)}%`
      : null;

  return (
    <article className="tool-live-card">
      <div className="tool-live-header">
        <div>
          <h2>Recompress a still AVIF with manual quality control</h2>
          <p>
            Fine-tune AVIF quality, adjust the runtime effort hint, and compare the resulting file
            size before you download it.
          </p>
        </div>
        <span className="tool-live-badge">Quality</span>
      </div>

      {!isSupported ? (
        <p className="support-feedback support-feedback-error">
          This browser cannot process AVIF locally yet. Use a modern Chromium or Safari build with
          ImageDecoder and OffscreenCanvas support.
        </p>
      ) : null}

      <div className="tool-live-grid">
        <div className="tool-live-uploader">
          <button
            className={`tool-live-dropzone ${isDragActive ? 'tool-live-dropzone-active' : ''}`}
            disabled={!isSupported}
            onClick={() => inputRef.current?.click()}
            onDragEnter={() => setIsDragActive(true)}
            onDragLeave={() => setIsDragActive(false)}
            onDragOver={(event) => {
              event.preventDefault();
              setIsDragActive(true);
            }}
            onDrop={(event) => {
              event.preventDefault();
              setIsDragActive(false);
              void handleFileSelection(event.dataTransfer.files?.[0] ?? null);
            }}
            type="button"
          >
            <div>
              <strong>{source ? source.file.name : 'Choose one AVIF or drop it here'}</strong>
              <span>
                {source
                  ? `${formatFileSize(source.file.size)} • ${source.metadata.width} × ${source.metadata.height}`
                  : 'Still AVIF only'}
              </span>
            </div>
          </button>
          <input
            ref={inputRef}
            accept="image/avif,.avif"
            className="tool-live-file-input"
            disabled={!isSupported}
            onChange={(event) => void handleFileSelection(event.target.files?.[0] ?? null)}
            type="file"
          />

          <div className="tool-live-controls">
            <label className="tool-live-field">
              <span>Quality</span>
              <input
                max="100"
                min="1"
                onChange={(event) => {
                  setQuality(Math.max(1, Number(event.target.value) || 68));
                  resetOutputState();
                }}
                step="1"
                type="range"
                value={quality}
              />
              <small>{quality}</small>
            </label>
            <label className="tool-live-field">
              <span>Effort hint</span>
              <select
                onChange={(event) => {
                  setEffortPreset(event.target.value as AvifEffortPreset);
                  resetOutputState();
                }}
                value={effortPreset}
              >
                <option value="faster">Faster</option>
                <option value="balanced">Balanced</option>
                <option value="slower">Higher fidelity</option>
              </select>
            </label>
          </div>

          <div className="hero-actions tool-live-actions">
            <button
              className="button"
              disabled={!source || isProcessing || !isSupported}
              onClick={() => void runRecompression()}
              type="button"
            >
              {isProcessing ? 'Recompressing…' : 'Recompress AVIF'}
            </button>
            {downloadUrl && downloadName ? (
              <a className="button button-ghost" download={downloadName} href={downloadUrl}>
                Download AVIF
              </a>
            ) : null}
          </div>

          {creditMessage ? (
            <p className="support-feedback support-feedback-success">{creditMessage}</p>
          ) : null}
          {errorMessage ? (
            <p className="support-feedback support-feedback-error">{errorMessage}</p>
          ) : null}
        </div>

        <aside className="tool-live-panel">
          <h3>Recompression settings</h3>
          <div className="tool-live-stats">
            <div>
              <strong>Quality</strong>
              <span>{quality}</span>
            </div>
            <div>
              <strong>Effort hint</strong>
              <span>{effortPreset}</span>
            </div>
            <div>
              <strong>Output size</strong>
              <span>{resultSize ? formatFileSize(resultSize) : 'Not exported yet'}</span>
            </div>
            {sizeDelta ? (
              <div>
                <strong>Size delta</strong>
                <span>{sizeDelta}</span>
              </div>
            ) : null}
          </div>
        </aside>
      </div>

      <div className="tool-live-preview">
        <h3>Preview</h3>
        <div className="tool-image-compare-grid">
          <div className="tool-image-compare-card">
            <strong>Source AVIF</strong>
            <div className="tool-live-preview-frame">
              {source ? (
                <img alt="Source AVIF preview" src={source.previewUrl} />
              ) : (
                <div className="tool-editor-empty">
                  <strong>Source preview</strong>
                  <span>Load one AVIF to tune the export quality.</span>
                </div>
              )}
            </div>
          </div>
          <div className="tool-image-compare-card">
            <strong>Recompressed AVIF</strong>
            <div className="tool-live-preview-frame">
              {resultPreviewUrl ? (
                <img alt="Recompressed AVIF preview" src={resultPreviewUrl} />
              ) : (
                <div className="tool-editor-empty">
                  <strong>No export yet</strong>
                  <span>Run the recompressor to preview the result.</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </article>
  );
}
